import { useEffect, useRef, useState } from "react";
import { SpeakEngine } from "@/lib/speak";
import { AudioWave } from "@/components/AudioWave";
import { toast } from "sonner";

// Active call overlay used by both roles. Caller passes conversationId;
// callee passes sessionId (the accepted request).
export function SpeakCall({ mode, conversationId, sessionId, otherName, onClose }) {
  const [state, setState] = useState(mode === "caller" ? "starting" : "connecting");
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const audioRef = useRef(null);
  const engineRef = useRef(null);
  const teardownRef = useRef(null);

  useEffect(() => {
    // StrictMode/dev double-invokes effects; cancel any pending teardown so a
    // quick remount reuses the same live engine instead of ending the call.
    if (teardownRef.current) { clearTimeout(teardownRef.current); teardownRef.current = null; }

    if (!engineRef.current) {
      const engine = new SpeakEngine({
        onLocalStream: setLocalStream,
        onRemoteStream: setRemoteStream,
        onState: setState,
        onEnded: () => { toast("Call ended"); onClose(); },
        onPoorNetwork: () => toast("poor network"),
      });
      engineRef.current = engine;
      const run = mode === "caller" ? engine.startCaller(conversationId) : engine.startCallee(sessionId);
      run.catch((e) => {
        toast(e?.name === "NotAllowedError" ? "microphone access denied" : "could not open the line");
        onClose();
      });
    }

    const onUnload = () => { try { engineRef.current?.end(true); } catch (err) { console.debug("speakcall: unload end failed", err); } };
    window.addEventListener("beforeunload", onUnload);
    return () => {
      window.removeEventListener("beforeunload", onUnload);
      teardownRef.current = setTimeout(() => {
        engineRef.current?.end(true);
        engineRef.current = null;
      }, 500);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (remoteStream && audioRef.current) audioRef.current.srcObject = remoteStream;
  }, [remoteStream]);

  const label =
    state === "connected" ? "connected"
    : state === "waiting" ? `waiting for ${otherName} to accept…`
    : "connecting…";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6 fade-in"
      style={{ background: "rgba(0,0,0,0.85)" }}
      data-testid="speak-call"
    >
      <div className="w-full max-w-md p-8" style={{ background: "var(--bg-modal)", border: "1px solid var(--border)" }}>
        <span className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>speaking</span>
        <h3 className="font-head text-2xl mt-3" style={{ color: "var(--text-primary)" }} data-testid="speak-call-username">
          {otherName}
        </h3>
        <p className="text-xs tracking-widest mt-1 mb-6" style={{ color: "var(--accent)" }} data-testid="speak-call-state">{label}</p>
        <div className="flex flex-col gap-3">
          <AudioWave stream={remoteStream} testId="speak-remote-wave" />
          <AudioWave stream={localStream} height={44} testId="speak-local-wave" />
        </div>
        <audio ref={audioRef} autoPlay />
        <button
          onClick={() => { engineRef.current?.end(true); onClose(); }}
          data-testid="speak-hangup-btn"
          className="btn w-full py-3 mt-6 text-sm tracking-widest"
        >
          HANG UP
        </button>
      </div>
    </div>
  );
}
